// ===========================================
// Phone & Messaging Verification Section
// ===========================================
// Displays verification status for phone-based messaging
// services (SMS, WhatsApp, etc.) on the Account Settings page.
// Each service shows its status and any required actions.

import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Sms as SmsIcon,
  WhatsApp as WhatsAppIcon,
  MessageOutlined as MessageIcon,
} from '@mui/icons-material';
import { CARD_COLORS } from './account.constants';
import {
  useExtendedNotificationPreferences,
  useNotificationServiceStatus,
  useUpdatePhone,
  useSendVerificationCode,
  useVerifyPhoneCode,
  useOptInWhatsApp,
} from '../../../hooks/useNotifications';
import { useUserProfile } from './account.hooks';

interface Props {
  isFunMode: boolean;
}

export default function PhoneVerificationSection({ isFunMode }: Props) {
  const [codeSent, setCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  // Queries
  const { data: extendedData } = useExtendedNotificationPreferences();
  const { data: serviceStatus } = useNotificationServiceStatus();
  const { data: userProfile } = useUserProfile();

  // Mutations
  const updatePhone = useUpdatePhone();
  const sendVerificationCode = useSendVerificationCode();
  const verifyPhoneCode = useVerifyPhoneCode();
  const optInWhatsApp = useOptInWhatsApp();

  const preferences = extendedData?.preferences;
  const userPhone = userProfile?.phone || preferences?.phoneNumber || null;
  const hasPhone = !!userPhone;
  const phoneVerified = preferences?.phoneVerified ?? false;
  const phoneSynced = !!preferences?.phoneNumber;

  const smsAvailable = serviceStatus?.smsEnabled ?? false;
  const whatsappAvailable = serviceStatus?.whatsappEnabled ?? false;
  const anyServiceAvailable = smsAvailable || whatsappAvailable;

  const isUpdating =
    updatePhone.isPending ||
    sendVerificationCode.isPending ||
    verifyPhoneCode.isPending ||
    optInWhatsApp.isPending;

  // Strip spaces/dashes from phone, keep leading +
  const normalizePhone = (phone: string): string =>
    phone.startsWith('+')
      ? '+' + phone.slice(1).replace(/\D/g, '')
      : phone.replace(/\D/g, '');

  const handleSendVerificationCode = () => {
    if (!userPhone) return;
    if (!phoneSynced) {
      updatePhone.mutate(
        { phoneNumber: normalizePhone(userPhone) },
        {
          onSuccess: () => {
            sendVerificationCode.mutate(undefined, {
              onSuccess: () => setCodeSent(true),
            });
          },
        }
      );
    } else {
      sendVerificationCode.mutate(undefined, {
        onSuccess: () => setCodeSent(true),
      });
    }
  };

  const handleVerifyCode = () => {
    if (!verificationCode || verificationCode.length !== 6) return;
    verifyPhoneCode.mutate(
      { code: verificationCode },
      {
        onSuccess: () => {
          setCodeSent(false);
          setVerificationCode('');
        },
      }
    );
  };

  const handleWhatsAppOptIn = () => {
    optInWhatsApp.mutate();
  };

  // Don't render if no phone or no messaging services available
  if (!hasPhone || !anyServiceAvailable) return null;

  // Check if everything is already verified/opted-in
  const smsReady = !smsAvailable || phoneVerified;
  const whatsappReady = !whatsappAvailable || (phoneVerified && preferences?.whatsappOptedIn);
  if (smsReady && whatsappReady) return null;

  return (
    <Card
      sx={{
        mb: 3,
        ...(isFunMode && {
          borderRadius: 4,
          border: `3px solid ${CARD_COLORS.profile.border}`,
          bgcolor: CARD_COLORS.profile.bg,
          boxShadow: '4px 4px 0px rgba(0,0,0,0.08)',
        }),
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box
            sx={{
              bgcolor: isFunMode ? `${CARD_COLORS.profile.border}30` : 'info.light',
              borderRadius: '50%',
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isFunMode ? CARD_COLORS.profile.border : 'info.main',
              flexShrink: 0,
            }}
          >
            <MessageIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              Phone & Messaging
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Verify your phone number and opt in to messaging services to receive notifications.
            </Typography>

            <Stack spacing={0}>
              {/* SMS Verification */}
              {smsAvailable && (
                <Box
                  sx={{
                    py: 1.5,
                    px: 2,
                    borderRadius: 1,
                    bgcolor: 'background.default',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                    <SmsIcon fontSize="small" sx={{ color: '#00897b' }} />
                    <Typography variant="subtitle2">SMS</Typography>
                    <Chip
                      label={phoneVerified ? 'Verified' : 'Not Verified'}
                      color={phoneVerified ? 'success' : 'warning'}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>

                  {!phoneVerified && (
                    <Box sx={{ ml: 4.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Verify {userPhone} to enable SMS notifications.
                      </Typography>

                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleSendVerificationCode}
                        disabled={isUpdating}
                        sx={{ mb: codeSent ? 1.5 : 0 }}
                      >
                        {(updatePhone.isPending || sendVerificationCode.isPending) ? (
                          <CircularProgress size={16} sx={{ color: 'inherit' }} />
                        ) : codeSent ? (
                          'Resend Code'
                        ) : (
                          'Send Verification Code'
                        )}
                      </Button>

                      {codeSent && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Enter the 6-digit code sent to {userPhone}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <TextField
                              size="small"
                              placeholder="000000"
                              value={verificationCode}
                              onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                setVerificationCode(val);
                              }}
                              inputProps={{
                                maxLength: 6,
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                style: { letterSpacing: '0.3em', fontFamily: 'monospace' },
                              }}
                              sx={{ width: 140 }}
                            />
                            <Button
                              variant="contained"
                              size="small"
                              onClick={handleVerifyCode}
                              disabled={isUpdating || verificationCode.length !== 6}
                            >
                              {verifyPhoneCode.isPending ? (
                                <CircularProgress size={16} sx={{ color: 'inherit' }} />
                              ) : (
                                'Verify'
                              )}
                            </Button>
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* WhatsApp Opt-In */}
              {whatsappAvailable && (
                <>
                  {smsAvailable && <Divider sx={{ my: 1 }} />}
                  <Box
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderRadius: 1,
                      bgcolor: 'background.default',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                      <WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} />
                      <Typography variant="subtitle2">WhatsApp</Typography>
                      {!phoneVerified ? (
                        <Chip
                          label="Phone Not Verified"
                          color="warning"
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      ) : preferences?.whatsappOptedIn ? (
                        <Chip
                          label="Opted In"
                          color="success"
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      ) : (
                        <Chip
                          label="Not Opted In"
                          color="warning"
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>

                    {!phoneVerified ? (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
                        Verify your phone number first to enable WhatsApp.
                      </Typography>
                    ) : !preferences?.whatsappOptedIn ? (
                      <Box sx={{ ml: 4.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          By opting in, you agree to receive notifications from Music 'n Me via WhatsApp.
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<WhatsAppIcon />}
                          onClick={handleWhatsAppOptIn}
                          disabled={isUpdating}
                          sx={{
                            color: '#25D366',
                            borderColor: '#25D366',
                            '&:hover': { borderColor: '#1da851', bgcolor: '#e8f5e9' },
                          }}
                        >
                          {optInWhatsApp.isPending ? (
                            <CircularProgress size={16} />
                          ) : (
                            'Opt In to WhatsApp'
                          )}
                        </Button>
                      </Box>
                    ) : null}
                  </Box>
                </>
              )}
            </Stack>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
